import React from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import qs from 'qs';

import useAuth from 'hooks/useAuth';
import xhr from 'xhr';
import sortConversations from 'pages/estudiantes/chat/components/sortConversations';

const ChatContext = React.createContext();

const DEFAULT_CHAT_PAGE_SIZE = 25;
const CHAT_PAGE_SIZE_WITH_USER_PARAM = 120;

function normalizeChatsPayload(res) {
  const body = res?.data;
  if (Array.isArray(body)) {
    return { list: body, meta: null };
  }
  if (body && Array.isArray(body.data)) {
    return { list: body.data, meta: body.meta || null };
  }
  return { list: [], meta: null };
}

function ChatProvider(props) {
  const [messages, setMessages] = React.useState([]);
  const [conversations, setConversations] = React.useState([]);
  const [messagesCount, setMessagesCount] = React.useState(0);
  const [isFetching, setIsFetching] = React.useState(true);
  const [isSearching, setIsSearching] = React.useState(false);
  const [chatPagination, setChatPagination] = React.useState({
    page: 1,
    pageSize: DEFAULT_CHAT_PAGE_SIZE,
    total: 0,
    pageCount: 0,
  });
  const [hasMoreChats, setHasMoreChats] = React.useState(false);
  const [isLoadingMoreChats, setIsLoadingMoreChats] = React.useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const chatBasePath = location.pathname.includes('/profesores/chat')
    ? '/plataforma/profesores/chat'
    : '/plataforma/estudiantes/chat';

  const conversationParam = searchParams.get('conversation');
  const otherUserParam = searchParams.get('user') || '';

  const getChats = React.useCallback(
    async ({ page = 1, append = false } = {}) => {
      if (!user?.id) return;

      const userParam = otherUserParam;
      const pageSize = userParam ? CHAT_PAGE_SIZE_WITH_USER_PARAM : DEFAULT_CHAT_PAGE_SIZE;

      try {
        if (!append) {
          setIsFetching(true);
        }

        const query = qs.stringify(
          {
            pagination: { page, pageSize },
            sort: ['createdAt:desc'],
          },
          { encodeValuesOnly: true }
        );

        const chatsResponseArr = await xhr(`/chats/user/${user.id}?${query}`);
        const { list: rawList, meta } = normalizeChatsPayload(chatsResponseArr);

        const chatsResponse = [...rawList].sort((a, b) => {
          return new Date(b.createdAt) - new Date(a.createdAt);
        });

        if (meta?.pagination) {
          setChatPagination(meta.pagination);
          setHasMoreChats(meta.pagination.page < meta.pagination.pageCount);
        } else {
          setHasMoreChats(false);
        }

        let nextList = chatsResponse;

        if (userParam) {
          const exists = chatsResponse.find((el) => {
            const isMember = el.members.find((el2) => String(el2.id) === String(userParam));
            return isMember;
          });

          if (exists) {
            const chatList = chatsResponse.filter((el) => el.id !== exists.id);
            nextList = [exists, ...chatList];
            setHasMoreChats(false);
          } else {
            const userResponse = await xhr(`/users/${userParam}`);
            nextList = [{ id: 'new', members: [userResponse.data], messages: [] }, ...chatsResponse];
            setHasMoreChats(false);
          }
        }

        setConversations((prev) => {
          if (append && !userParam) {
            const merged = [...prev, ...nextList];
            const byId = new Map(merged.map((c) => [c.id, c]));
            return sortConversations([...byId.values()], user?.id);
          }
          return sortConversations(nextList, user?.id);
        });
      } catch (error) {
        if (!append) {
          setConversations([]);
        }
      } finally {
        if (!append) {
          setIsFetching(false);
        }
      }
    },
    [user?.id, otherUserParam]
  );

  const loadMoreChats = React.useCallback(async () => {
    if (otherUserParam) return;
    if (!hasMoreChats || isLoadingMoreChats || isFetching) return;
    setIsLoadingMoreChats(true);
    try {
      const nextPage = (chatPagination.page || 1) + 1;
      await getChats({ page: nextPage, append: true });
    } catch (e) {
      // ignore
    } finally {
      setIsLoadingMoreChats(false);
    }
  }, [otherUserParam, hasMoreChats, isLoadingMoreChats, isFetching, chatPagination.page, getChats]);

  const resetChatPagination = React.useCallback(() => {
    setHasMoreChats(false);
    setChatPagination((prev) => ({
      ...prev,
      page: 1,
      pageCount: 1,
      total: 0,
    }));
  }, []);

  React.useEffect(() => {
    if (!user?.id) return undefined;
    getChats({ page: 1, append: false });
    return undefined;
  }, [user?.id, otherUserParam, getChats]);

  React.useEffect(() => {
    const getMessages = async () => {
      if (!conversationParam) return;

      if (conversationParam === 'new') {
        const chatResponse = await xhr.post('/chats', {
          data: {
            members: [searchParams.get('user'), user.id],
          },
        });

        setConversations((prev) =>
          prev.map((el) => {
            if (el.id === 'new') {
              return { ...el, id: chatResponse.data.data.id };
            }
            return el;
          })
        );

        return navigate(`${chatBasePath}?conversation=${chatResponse.data.data.id}`);
      }

      const response = await xhr(
        `/chat-messages?filters[chat][id][$eq]=${conversationParam}&pagination[limit]=15&sort[0]=createdAt:desc&populate=*`
      );

      setMessages(response.data.data.reverse());
    };

    getMessages();
  }, [conversationParam, chatBasePath, navigate, user?.id, searchParams]);

  return (
    <ChatContext.Provider
      value={{
        messages,
        setMessages,
        conversations,
        setConversations,
        messagesCount,
        setMessagesCount,
        isFetching,
        isSearching,
        setIsSearching,
        hasMoreChats,
        loadMoreChats,
        isLoadingMoreChats,
        resetChatPagination,
      }}
      {...props}
    />
  );
}

function useChat() {
  const context = React.useContext(ChatContext);

  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}

export { ChatProvider, useChat };
